import * as Sentry from '@sentry/vue';

/** Marks an error (and the response body the interceptors may re-throw) as already reported. */
const REPORTED = Symbol.for('moin.sentry.apiErrorReported');

/**
 * True for an error `reportApiError` already sent, so `beforeSend` can drop a second report of
 * the same failure. Matches the axios error itself, the response body the interceptors re-throw,
 * and errors that wrap either one: `ApiProtocolError` keeps the body as `details` (livechat
 * service via api-v2's `formatError`), standard wrappers use `cause`.
 */
export function wasReportedApiError(exception: unknown): boolean {
  if (isMarked(exception)) return true;
  const wrapped = exception as { details?: unknown; cause?: unknown } | null;
  return !!wrapped && typeof wrapped === 'object' && (isMarked(wrapped.details) || isMarked(wrapped.cause));
}

function isMarked(value: unknown): boolean {
  return !!value && typeof value === 'object' && (value as any)[REPORTED] === true;
}

function markReported(value: unknown): void {
  if (value && typeof value === 'object' && Object.isExtensible(value)) {
    Object.defineProperty(value, REPORTED, { value: true, enumerable: false });
  }
}

/**
 * Report failed API calls (5xx) to Sentry.
 *
 * The API services catch and log their errors, so without this a broken endpoint never reaches
 * Sentry (it only sees uncaught errors). Call it first thing in each axios response-error
 * interceptor (`src/services/api.ts`, `src/services/api-v2.ts`).
 *
 * Kept in its own module, importing nothing but the SDK: the API services are imported by the
 * stores that `src/sentry.ts` imports, so putting this there would create an import cycle.
 *
 * - Only 5xx: 4xx are expected outcomes (validation, permissions, 401 handled by the interceptor),
 *   and network / CORS failures without a response are handled as "connection lost".
 * - Grouped by method + route + status: IDs in the URL are replaced by `:id`, so one failing
 *   endpoint is one issue, not one per bot or resource.
 * - No response body is sent; only the backend's error `code` and a shortened `message`.
 * - A no-op while Sentry is not initialised (no DSN for the mode).
 */
export function reportApiError(error: any): void {
  if (!Sentry.getClient()) return;

  const status: number | undefined = error?.response?.status;
  if (!status || status < 500) return;

  if (isMarked(error)) return;
  markReported(error);
  markReported(error?.response?.data);

  const method = String(error?.config?.method ?? 'get').toUpperCase();
  const route = normalizeRoute(error?.config?.url);
  const data = error?.response?.data;

  Sentry.withScope((scope) => {
    scope.setTags({
      'api.method': method,
      'api.route': route,
      'api.status': String(status),
    });
    scope.setContext('api', {
      method,
      route,
      status,
      code: typeof data?.code === 'string' || typeof data?.code === 'number' ? data.code : undefined,
      message: typeof data?.message === 'string' ? data.message.slice(0, 200) : undefined,
    });
    scope.setFingerprint(['api-error', method, route, String(status)]);

    const apiError = new Error(`${status} ${method} ${route}`);
    apiError.name = 'ApiServerError';
    Sentry.captureException(apiError);
  });
}

/** Path without host or query, with ID-like segments replaced by `:id`. */
function normalizeRoute(url: unknown): string {
  if (typeof url !== 'string' || !url) return 'unknown';

  let path = url;
  try {
    path = new URL(url, 'http://placeholder').pathname;
  } catch {
    path = url.split('?')[0];
  }

  return path
    .split('/')
    .map((segment) => (isIdLike(segment) ? ':id' : segment))
    .join('/');
}

/**
 * Numbers, Mongo ObjectIds, UUIDs, and the hub's 8-character bot / channel IDs (`W1nLTuBW`,
 * `XJrQtAPO`). Route words stay as they are, camelCase ones included (`agentEval`,
 * `blockedChannels`, `k2names`): an 8-char segment only counts as an ID if it has a digit or at
 * least two capitals.
 */
function isIdLike(segment: string): boolean {
  if (!segment) return false;
  if (/^\d+$/.test(segment)) return true;
  if (/^[0-9a-f]{24}$/i.test(segment)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return true;
  return /^[A-Za-z0-9]{8}$/.test(segment) && (/\d/.test(segment) || (segment.match(/[A-Z]/g)?.length ?? 0) >= 2);
}
